import { z } from "zod";
import { dateBasisSchema } from "../entities/dateBasis.js";
import { DEFAULT_DATE_RANGE_DAYS } from "../entities/dateRange.js";
import { groupIdSchema } from "../entities/group.js";
import { teammateIdSchema } from "../entities/teammate.js";
import { topicIdSchema } from "../entities/topic.js";
const dateFilterSchema = z.object({
  after: z.iso.datetime({ offset: true }).describe(
    "Include interactions whose date (per date_basis) is after this ISO date"
  ),
  before: z.iso.datetime({ offset: true }).describe(
    "Include interactions whose date (per date_basis) is before this ISO date"
  )
});
const optionalPartialDateFilterSchema = z.object({
  after: z.iso.datetime({ offset: true }).nullish(),
  before: z.iso.datetime({ offset: true }).nullish()
}).nullish().describe(
  `Include interactions whose date (per date_basis) is within the specified date range, or null to use the default (last ${DEFAULT_DATE_RANGE_DAYS} days).`
);
const optionalDateBasisFilterSchema = dateBasisSchema.nullish().describe(
  "Which date the date range applies to. 'last_activity' (the default, used when null) is the interaction's most recent call. 'creation_date' is when the interaction came into being: its first call, unless the playbook reports interactions under a CRM date field such as an opportunity created date \u2014 get-playbook-details names it. Use 'creation_date' for questions about interactions created, opened, or started in a period, regardless of when their latest call happened."
);
const optionalTeammatesFilterSchema = z.array(teammateIdSchema).readonly().nullish().describe(
  "Teammate IDs (UUIDs) to filter by, or null to include all teammates."
);
const optionalTeammateGroupsFilterSchema = z.array(groupIdSchema).readonly().nullish().describe(
  "Teammate group IDs (UUIDs) to filter by: the interaction's teammate must belong to at least one listed group. Resolve group names to ids with the list-teammate-groups tool. AND-combined with teammate_ids. Null for no group filter."
);
const optionalIngestedDateFilterSchema = z.object({
  after: z.iso.datetime({ offset: true }).nullish(),
  before: z.iso.datetime({ offset: true }).nullish()
}).nullish().describe(
  "Optional ingested-date filter: when a completed recording became available in the system, distinct from `date` (when the call happened). Rarely needed \u2014 usually pass null. AND-combined with `date`: it only narrows to calls matching both bounds, never adds calls outside the `date` range. Its main use is the ingestion-lag pattern: on a very frequent run, widen `date` and set `ingested_date.after` to the last run to pick up recordings that arrived late without re-analyzing earlier calls. Each bound is a full ISO datetime with offset, inclusive; provide after, before, or both. Null for no filter."
);
const playbookCriterionFilterSchema = z.union([
  z.object({
    value_type: z.literal("yes_no").describe("Criteria value type"),
    id: z.uuid().describe("Criteria ID (UUID)"),
    value: z.enum(["yes", "no"]).describe("Criteria value")
  }),
  z.object({
    value_type: z.literal("1_5_score").describe("Criteria value type"),
    id: z.uuid().describe("Criteria ID (UUID)"),
    op: z.enum(["=", "<", "<=", ">", ">="]).describe("Comparison operator"),
    value: z.enum(["1", "2", "3", "4", "5"]).describe("Criteria score (1-5)")
  }),
  z.object({
    value_type: z.enum(["yes_no", "1_5_score"]).describe("Criteria value type \u2014 must match the criteria's grading type"),
    id: z.uuid().describe("Criteria ID (UUID)"),
    op: z.enum(["is_applicable", "is_not_applicable"]).describe(
      "Applicability predicate. 'is_applicable' matches only interactions where this criteria was actually graded \u2014 i.e. the call reached the phase where the criteria is in play \u2014 and excludes interactions where it came back N/A (e.g. short voicemails that hung up before the relevant part of the call). 'is_not_applicable' matches the inverse."
    )
  })
]);
const optionalPlaybookCriteriaFilterSchema = z.array(playbookCriterionFilterSchema).nullish().describe(
  "Playbook criteria values/applicability to filter by, combined per playbook_criteria_match, or null for no filter. A criteria graded N/A never matches a value predicate."
);
const optionalPlaybookCriteriaMatchFilterSchema = z.enum(["all", "any"]).nullish().describe(
  "How playbook_criteria entries combine: 'all' (the default, used when null) requires every entry to match; 'any' requires at least one, e.g. interactions that missed any of several criteria. With 'any', an is_applicable entry is ORed too, so leave it out. The other filters still AND-combine with the result."
);
const customFieldNameSchema = z.string().describe("Custom field name");
const customFieldFilterMatchSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("text"),
    value: z.string().describe("Expected exact custom field value")
  }).describe(
    "Exact raw-string equality. Use for text, boolean, or user-reference custom fields, not for numeric comparisons or calendar-day matching."
  ),
  z.object({
    kind: z.literal("number"),
    op: z.enum(["=", "<", "<=", ">", ">="]).describe("Comparison operator"),
    value: z.number().describe("Numeric comparison value")
  }).describe(
    "Numeric comparison, including '=' equality, for number custom fields or text fields whose values represent numbers."
  ),
  z.object({
    kind: z.literal("date"),
    op: z.enum(["after", "before"]).describe("Calendar-day comparison; the named day itself is excluded"),
    value: z.iso.date().describe("Calendar date (YYYY-MM-DD)")
  }).describe(
    "Calendar-day comparison for custom fields of type date or date_and_time."
  ),
  z.object({
    kind: z.literal("date_range"),
    start: z.iso.date().describe("First included day (YYYY-MM-DD)"),
    end: z.iso.date().describe("Last included day (YYYY-MM-DD)")
  }).describe(
    "Inclusive calendar-day range for custom fields of type date or date_and_time. To match one calendar day, pass the same date as start and end."
  )
]);
const customFieldFilterSchema = z.object({
  field_name: customFieldNameSchema,
  match: customFieldFilterMatchSchema.describe(
    "Which comparison to apply: kind 'text' for exact raw-string equality, 'number' for every numeric comparison including '=', 'date' for before/after, or 'date_range' for inclusive calendar-day matching."
  )
});
const numericCustomFieldFilterDescription = "Number matches parse string-backed values after removing optional dollar, pound, or euro signs, commas, and whitespace. Values that still cannot be parsed as numbers do not match.";
const customFieldValueDiscoveryDescription = "For a text match, read the field's real stored values with the list-custom-field-values tool rather than guessing. Number and date matches use typed values directly.";
const dateCustomFieldFilterDescription = "Date and date_range matches apply only to fields of type date or date_and_time; date_and_time values are compared by their calendar day in the company time zone. To match one calendar day, use date_range with the same start and end.";
const optionalContactCustomFieldsFilterSchema = z.array(customFieldFilterSchema).nullish().describe(
  `Contact custom field values to filter interactions by, matched via the interaction's linked contact. ${numericCustomFieldFilterDescription} ${dateCustomFieldFilterDescription} Only use field names listed as contact custom fields. ${customFieldValueDiscoveryDescription} Null for no filter.`
);
const optionalCustomerInteractionCustomFieldsFilterSchema = z.array(customFieldFilterSchema).nullish().describe(
  `Customer interaction custom field values to filter by, set directly on the interaction. ${numericCustomFieldFilterDescription} ${dateCustomFieldFilterDescription} Only use field names listed as customer interaction custom fields. ${customFieldValueDiscoveryDescription} Null for no filter.`
);
const optionalDataExtractionCustomFieldsFilterSchema = z.array(customFieldFilterSchema).nullish().describe(
  `Data extraction custom field values to filter by, extracted by AI from the interaction's transcript and set directly on the interaction. ${numericCustomFieldFilterDescription} ${dateCustomFieldFilterDescription} Only use field names listed as data extraction custom fields. ${customFieldValueDiscoveryDescription} Null for no filter.`
);
const optionalOutcomeFilterSchema = z.object({
  type: z.enum(["preset", "custom"]).describe("Outcome type"),
  id: z.string().describe("Outcome ID"),
  value: z.string().describe("Outcome value")
}).nullish().describe("Outcome to filter by, or null for any outcome.");
const signalMetadataFilterSchema = z.union([
  z.object({
    type: z.literal("enum").describe("Enum metadata field"),
    op: z.literal("equals").describe("Equality match"),
    field_id: z.uuid().describe("Metadata field ID (UUID)"),
    value: z.string().describe("Expected enum value")
  }),
  z.object({
    type: z.literal("number").describe("Numeric metadata field"),
    op: z.literal("range").describe("Half-open range [lower, upper)"),
    field_id: z.uuid().describe("Metadata field ID (UUID)"),
    lower: z.number().describe("Inclusive lower bound"),
    upper: z.number().describe("Exclusive upper bound")
  }),
  z.object({
    type: z.literal("number").describe("Numeric metadata field"),
    op: z.enum(["<", "<=", ">", ">=", "="]).describe("Comparison operator"),
    field_id: z.uuid().describe("Metadata field ID (UUID)"),
    value: z.number().describe("Threshold value")
  })
]);
const signalFilterSchema = z.object({
  id: z.uuid().describe("Signal ID (UUID)"),
  metadata_filters: z.array(signalMetadataFilterSchema).nullish().describe(
    "Optional metadata field constraints applied to this signal's matches. Multiple entries are AND-combined."
  )
});
const optionalSignalsFilterSchema = z.array(signalFilterSchema).nullish().describe(
  "Signals to filter by. Multiple entries are AND-combined: an interaction must have matched every listed signal (and satisfy each entry's metadata filters). Pass null/omit for no signal filtering."
);
const optionalTopicsFilterSchema = z.array(topicIdSchema).readonly().nullish().describe(
  "Topic IDs (UUIDs) to filter by. Multiple entries are AND-combined: an interaction must have a discussion moment matched to every listed topic. Obtain ids from the list-topics tool; only leaf topics have matches, and an unknown id matches nothing. Pass null/omit for no topic filtering."
);
const optionalCallDurationFilterSchema = z.array(
  z.object({
    op: z.enum(["<", "<=", ">", ">="]).describe("Comparison operator against the call's duration"),
    value_seconds: z.number().int().nonnegative().describe("Duration threshold in seconds")
  })
).nullish().describe(
  "Filter interactions by total call recording duration, in seconds. Each entry is AND-combined, so pass two entries to express a band (e.g. `>=` 30 and `<` 120 for calls between 30 seconds and 2 minutes). Useful for isolating ultra-short calls. Note this measures raw recording length and does not by itself distinguish a live pickup from a voicemail or no-answer \u2014 combine it with playbook-criteria applicability filters when you need calls where a real conversation started. Null for no duration filter."
);
const optionalCustomerExperienceScoreFilterSchema = z.array(
  z.object({
    op: z.enum(["=", "<", "<=", ">", ">="]).describe("Comparison operator against the interaction's CX score"),
    value: z.number().int().min(1).max(5).describe("CX score threshold on the 1-5 scale")
  })
).nullish().describe(
  "Filter interactions by customer experience (CX) score, a 1-5 rating of how the customer experienced the interaction (5 = excellent). Each entry is AND-combined, so pass two entries to express a band. Interactions without a CX score (voicemails, no customer voice, or scoring not enabled) never match. Null for no CX score filter."
);
const baseFilterShape = {
  date_basis: optionalDateBasisFilterSchema,
  playbook_criteria: optionalPlaybookCriteriaFilterSchema,
  playbook_criteria_match: optionalPlaybookCriteriaMatchFilterSchema,
  contact_custom_fields: optionalContactCustomFieldsFilterSchema,
  customer_interaction_custom_fields: optionalCustomerInteractionCustomFieldsFilterSchema,
  data_extraction_custom_fields: optionalDataExtractionCustomFieldsFilterSchema,
  signals: optionalSignalsFilterSchema,
  topics: optionalTopicsFilterSchema,
  call_duration: optionalCallDurationFilterSchema,
  customer_experience_score: optionalCustomerExperienceScoreFilterSchema
};
const teammateFilterShape = {
  teammate_ids: optionalTeammatesFilterSchema,
  teammate_group_ids: optionalTeammateGroupsFilterSchema
};
const windowFilterShape = {
  date: optionalPartialDateFilterSchema,
  ingested_date: optionalIngestedDateFilterSchema
};
const requiredWindowFilterShape = {
  date: dateFilterSchema,
  ingested_date: optionalIngestedDateFilterSchema
};
const contactNameFilterSchema = z.string().max(150).nullish().describe(
  "Customer/contact name to filter interactions by. Matching interactions are found via fuzzy name search, so minor spelling differences are tolerated. Use the name exactly as the user wrote it \u2014 do not infer, expand, or add alternates. Use the same value in both count-customer-interactions and research-customer-interactions calls."
);
const contactPhoneNumberFilterSchema = z.string().max(30).nullish().describe(
  "Customer/contact phone number to filter interactions by. Matched exactly against the contact's stored phone number after normalization, so formatting differences (spaces, dashes, parentheses) are tolerated. A number without a country code is interpreted as US \u2014 include the country code (e.g. +44...) for non-US numbers. Use the same value across the count-, list-, and research-customer-interactions calls."
);
const MAX_TRANSCRIPT_SEARCH_CHARS = 300;
const MAX_TRANSCRIPT_SEARCH_QUERIES = 4;
const transcriptSearchSchema = z.object({
  queries: z.array(z.string().max(MAX_TRANSCRIPT_SEARCH_CHARS)).min(1).max(MAX_TRANSCRIPT_SEARCH_QUERIES).describe(
    'Search text variants for transcript chunks. Provide one query for a narrow search, or 2-4 variants when the same concept could appear in several phrasings. An interaction matches if it matches any query; duplicate interactions are collapsed before normal filters are applied.\n- keyword: each individual query requires all of its tokens to appear. Use multiple queries for alternate wording, spelling, acronyms, or expanded names.\n- semantic: each query should be a concrete transcript-like phrase a caller or customer might actually say, not analysis about the transcript \u2014 e.g. ["That price is higher than I expected. Is there a cheaper option?", "This is too expensive for our budget right now.", "Another company said they could do the same thing for less."].\n- hybrid: use when you have a specific term to anchor the search, but relevant calls may describe the same topic without saying that exact phrase.'
  ),
  type: z.enum(["keyword", "semantic", "hybrid"]).describe(
    "Transcript search mode. Pick one:\n- keyword: exact-term BM25 search. Use when the user gave distinctive words that should appear verbatim in the transcript.\n- semantic: meaning-based vector search. Use for concepts, themes, sentiment, objections, confusion, excitement, or behavioral questions; this is the default when the user did not provide exact words.\n- hybrid: combined keyword and semantic ranking using the same query text for both. Use when you have a specific term to anchor the search, but relevant calls may describe the same topic without saying that exact phrase."
  )
}).optional().describe(
  "Optional transcript pre-filter. Narrows interactions to recordings with matching transcript chunks, then applies the other interaction filters. Provide `type` and `queries`. Do not include date, teammate, outcome, signal, or custom-field constraints in transcript queries; use the normal filters for those. Use the same `transcript_search` value in both count-customer-interactions and research-customer-interactions calls."
);
export {
  MAX_TRANSCRIPT_SEARCH_CHARS,
  MAX_TRANSCRIPT_SEARCH_QUERIES,
  baseFilterShape,
  contactNameFilterSchema,
  contactPhoneNumberFilterSchema,
  dateFilterSchema,
  optionalCallDurationFilterSchema,
  optionalContactCustomFieldsFilterSchema,
  optionalCustomerExperienceScoreFilterSchema,
  optionalCustomerInteractionCustomFieldsFilterSchema,
  optionalDataExtractionCustomFieldsFilterSchema,
  optionalDateBasisFilterSchema,
  optionalIngestedDateFilterSchema,
  optionalOutcomeFilterSchema,
  optionalPartialDateFilterSchema,
  optionalPlaybookCriteriaFilterSchema,
  optionalPlaybookCriteriaMatchFilterSchema,
  optionalSignalsFilterSchema,
  optionalTeammateGroupsFilterSchema,
  optionalTeammatesFilterSchema,
  optionalTopicsFilterSchema,
  requiredWindowFilterShape,
  teammateFilterShape,
  transcriptSearchSchema,
  windowFilterShape
};
//# sourceMappingURL=customerInteractionFilters.js.map
